import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Pagination,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { del, get, patch, post } from "src/utils/networkUtils";

const COUNTRY_LIMIT = 20;
const AREA_LIMIT = 100;

function normalizeRows(payload) {
  const items = payload?.data?.items ?? payload?.items ?? [];
  return Array.isArray(items) ? items : [];
}

function normalizePagination(
  payload,
  fallbackPage = 1,
  fallbackLimit = COUNTRY_LIMIT
) {
  const pagination = payload?.data?.pagination ?? payload?.pagination;
  return {
    page: Number(pagination?.page ?? fallbackPage),
    limit: Number(pagination?.limit ?? fallbackLimit),
    total: Number(pagination?.total ?? 0),
    totalPages: Number(pagination?.totalPages ?? 1),
  };
}

export default function CountriesTabPage() {
  const [countries, setCountries] = useState([]);
  const [areas, setAreas] = useState([]);
  const [countryPage, setCountryPage] = useState(1);
  const [countryPagination, setCountryPagination] = useState({
    page: 1,
    limit: COUNTRY_LIMIT,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dialogMode, setDialogMode] = useState(null); // "create" | "edit" | "delete" | null
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [form, setForm] = useState({
    name: "",
    iso_code: "",
    iso3_code: "",
    area_id: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeDialog = () => {
    setDialogMode(null);
    setSelectedCountry(null);
    setForm({ name: "", iso_code: "", iso3_code: "", area_id: "" });
  };

  const fetchCountries = useCallback(async () => {
    const response = await get(
      `/admin/country?page=${countryPage}&limit=${COUNTRY_LIMIT}`
    );
    setCountries(normalizeRows(response));
    setCountryPagination(
      normalizePagination(response, countryPage, COUNTRY_LIMIT)
    );
  }, [countryPage]);

  const fetchAreas = useCallback(async () => {
    const response = await get(`/admin/area?page=1&limit=${AREA_LIMIT}`);
    setAreas(normalizeRows(response));
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchCountries()
      .catch((error) => {
        console.error("Failed to fetch countries:", error);
        showSnackbar("Failed to load countries data", "error");
      })
      .finally(() => setLoading(false));
  }, [fetchCountries]);

  useEffect(() => {
    fetchAreas().catch((error) => {
      console.error("Failed to fetch areas:", error);
      showSnackbar("Failed to load areas", "error");
    });
  }, [fetchAreas]);

  const openCreate = () => {
    setDialogMode("create");
    setSelectedCountry(null);
    setForm({ name: "", iso_code: "", iso3_code: "", area_id: "" });
  };

  const openEdit = (country) => {
    setDialogMode("edit");
    setSelectedCountry(country);
    setForm({
      name: country.name ?? "",
      iso_code: country.iso_code ?? "",
      iso3_code: country.iso3_code ?? "",
      area_id: country.area_id ?? "",
    });
  };

  const openDelete = (country) => {
    setDialogMode("delete");
    setSelectedCountry(country);
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    iso_code: form.iso_code.trim().toUpperCase() || undefined,
    iso3_code: form.iso3_code.trim().toUpperCase() || undefined,
    area_id: form.area_id || undefined,
  });

  const handleCreate = async () => {
    if (!form.name.trim()) {
      showSnackbar("Country name is required", "error");
      return;
    }
    setSubmitting(true);
    try {
      await post("/admin/country", buildPayload());
      showSnackbar("Country created successfully");
      closeDialog();
      setLoading(true);
      await Promise.all([fetchCountries(), fetchAreas()]);
    } catch (error) {
      console.error("Create country failed:", error);
      showSnackbar("Failed to create country", "error");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedCountry?.id) return;
    if (!form.name.trim()) {
      showSnackbar("Country name is required", "error");
      return;
    }
    setSubmitting(true);
    try {
      await patch(`/admin/country/${selectedCountry.id}`, buildPayload());
      showSnackbar("Country updated successfully");
      closeDialog();
      setLoading(true);
      await Promise.all([fetchCountries(), fetchAreas()]);
    } catch (error) {
      console.error("Update country failed:", error);
      showSnackbar("Failed to update country", "error");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCountry?.id) return;
    setSubmitting(true);
    try {
      await del(`/admin/country/${selectedCountry.id}`);
      showSnackbar("Country deleted successfully");
      closeDialog();
      setLoading(true);
      await Promise.all([fetchCountries(), fetchAreas()]);
    } catch (error) {
      console.error("Delete country failed:", error);
      showSnackbar("Failed to delete country", "error");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Add New Country
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Country Name</TableCell>
              <TableCell>ISO Code</TableCell>
              <TableCell>ISO3 Code</TableCell>
              <TableCell>Area</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {countries.map((country) => (
              <TableRow key={country.id} hover>
                <TableCell>{country.name}</TableCell>
                <TableCell>{country.iso_code || "-"}</TableCell>
                <TableCell>{country.iso3_code || "-"}</TableCell>
                <TableCell>{country.area?.name || "-"}</TableCell>
                <TableCell>
                  <IconButton
                    aria-label={`Edit ${country.name}`}
                    color="primary"
                    onClick={() => openEdit(country)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label={`Delete ${country.name}`}
                    color="error"
                    onClick={() => openDelete(country)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {!loading && countries.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  No countries found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {countryPagination.totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
          <Pagination
            page={countryPage}
            count={countryPagination.totalPages}
            color="primary"
            shape="rounded"
            onChange={(_, page) => setCountryPage(page)}
          />
        </Box>
      )}

      <Dialog
        open={dialogMode === "create"}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Country</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Country Name"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="ISO Code (2)"
              value={form.iso_code}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, iso_code: e.target.value }))
              }
              inputProps={{ maxLength: 2 }}
              fullWidth
            />
            <TextField
              label="ISO3 Code (3)"
              value={form.iso3_code}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, iso3_code: e.target.value }))
              }
              inputProps={{ maxLength: 3 }}
              fullWidth
            />
            <TextField
              label="Area"
              select
              value={form.area_id}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, area_id: e.target.value }))
              }
              fullWidth
            >
              <MenuItem value="">No Area</MenuItem>
              {areas.map((area) => (
                <MenuItem key={area.id} value={area.id}>
                  {area.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={submitting}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogMode === "edit"}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Country</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Country Name"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="ISO Code (2)"
              value={form.iso_code}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, iso_code: e.target.value }))
              }
              inputProps={{ maxLength: 2 }}
              fullWidth
            />
            <TextField
              label="ISO3 Code (3)"
              value={form.iso3_code}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, iso3_code: e.target.value }))
              }
              inputProps={{ maxLength: 3 }}
              fullWidth
            />
            <TextField
              label="Area"
              select
              value={form.area_id}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, area_id: e.target.value }))
              }
              fullWidth
            >
              <MenuItem value="">No Area</MenuItem>
              {areas.map((area) => (
                <MenuItem key={area.id} value={area.id}>
                  {area.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={handleEdit}
            variant="contained"
            disabled={submitting}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogMode === "delete"}
        onClose={closeDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Country</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete{" "}
            <strong>{selectedCountry?.name ?? "this country"}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={submitting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
